import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Tab, Tabs, Chip, IconButton, Tooltip, Alert, CircularProgress } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { agentService } from '../services/agentService';
import { ChatCompletionsTab } from '../components/api-explorer/ChatCompletionsTab';
import { ResponsesTab } from '../components/api-explorer/ResponsesTab';
import { AssistantsTab } from '../components/api-explorer/AssistantsTab';
import type { AgentDefinitionDto } from '../types/agent';

export const ApiExplorerDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [agent, setAgent] = useState<AgentDefinitionDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (slug) loadAgent(slug);
  }, [slug]);

  const loadAgent = async (agentSlug: string) => {
    setLoading(true);
    setError(null);
    try {
      const a = await agentService.getAgentBySlug(agentSlug);
      setAgent(a);
    } catch {
      setError(`Agent with slug "${agentSlug}" not found.`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !agent || !slug) {
    return (
      <Box sx={{ p: 6 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error ?? 'Agent not found.'}</Alert>
        <Tooltip title="Back to API Explorer">
          <IconButton onClick={() => navigate('/api-explorer')}><ArrowBackIcon /></IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 6, animation: 'fade-in 300ms ease-out' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Tooltip title="Back to API Explorer">
          <IconButton onClick={() => navigate('/api-explorer')}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h5" fontWeight={700}>{agent.name}</Typography>
            <Chip
              label={slug}
              size="small"
              variant="outlined"
              sx={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {agent.description ?? 'Explore Chat Completions, Responses, and Assistants API data.'}
          </Typography>
        </Box>
      </Box>

      {/* API Tabs */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 3,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              minHeight: 48,
              px: 3,
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
          }}
        >
          <Tab label="Chat Completions" />
          <Tab label="Responses" />
          <Tab label="Assistants" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box
        sx={{
          p: 3,
          borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
          border: '1px solid var(--glass-border)',
          background: 'var(--glass-bg-subtle)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          minHeight: 300,
        }}
      >
        {activeTab === 0 && <ChatCompletionsTab agentId={agent.id} agentSlug={slug} />}
        {activeTab === 1 && <ResponsesTab agentId={agent.id} agentSlug={slug} />}
        {activeTab === 2 && <AssistantsTab agentId={agent.id} agentSlug={slug} />}
      </Box>
    </Box>
  );
};
